import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Table,
  TableRow,
  TableBody,
  useTheme,
  Box,
  Typography,
  TableContainer,
  IconButton,
  Checkbox,
} from "@mui/material";
import VantageListHead from "../../../../../../../../component/ListHead/SignalList";
import {
  Tablecell,
  applySortFilter,
  calculateAndFormatNoAbsInt,
  findBrokerAbb,
  findFundAbbById,
  findStrategy,
  getComparator,
  hashAction,
  hashPosition,
} from "../../../../../../../../component/Methods";
import { useSelector } from "react-redux";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import CrossingTradesTableSkeleton from "../../../../../../../../component/spinner/CrossingTradesTableSkeleton";
import { selectActiveRequests } from "../../../../../../../../Redux/Reducers/selectors";
import { useRebalancerState } from "../../../../../../../../hooks/useOptimizedState";

const CrossingTradesTable = forwardRef(
  ({ tableHeader, crossingTrades }, ref) => {
    const theme = useTheme();
    const { funds, strategies, brokers } = useRebalancerState();
  const activeRequests = useSelector(selectActiveRequests);


    const [data, setData] = useState([]);
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("ticker");
    const [selectedFund, setSelectedFund] = useState("All");
    const [uniqueFunds, setUniqueFunds] = useState([]);
    const [toggleFilterByCustodian, setToggleFilterByCustodian] =
      useState(false);
    const [ignoredRows, setIgnoredRows] = useState(new Set());

    const handleRequestSort = (event, property) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    };

    const handleIgnoreRow = (index) => {
      const newIgnoredRows = new Set(ignoredRows);
      if (newIgnoredRows?.has(index)) {
        newIgnoredRows?.delete(index);
      } else {
        newIgnoredRows?.add(index);
      }
      setIgnoredRows(newIgnoredRows);
    };

    const handleIgnoreAll = () => {
      if (ignoredRows?.size === data?.length) {
        setIgnoredRows(new Set());
      } else {
        const allIndices = data?.map((_, index) => index);
        setIgnoredRows(new Set(allIndices));
      }
    };

    const getIgnoredTrades = () => {
      return data?.filter((_, index) => ignoredRows?.has(index));
    };

    const getIgnoredTradeIds = () => {
      const ignoredTrades = data?.filter((_, index) => ignoredRows?.has(index));
      const tradeIds = ignoredTrades
        ?.map((trade) => {
          return trade?.originalTradeId || trade?.tradeId || trade?.id || null;
        })
        .filter(Boolean);

      return tradeIds;
    };
    React.useImperativeHandle(ref, () => ({
      getIgnoredTrades,
      getIgnoredTradeIds,
    }));

    useEffect(() => {
      const filteredOrders = crossingTrades?.map((order) => {
        const strategy = findStrategy(order?.strategy, strategies);
        const ticker = order?.ticker.stock
          ? `${order?.ticker?.symbol}/${order?.ticker?.stock}`
          : order?.ticker?.symbol;
        const tag = order?.crossingTag;
        let action = hashAction(order?.action) + hashPosition(order?.position);
        const shareCount = order?.shareCount;
        const custodian = order?.custodian;
        const broker = findBrokerAbb(order?.broker, brokers);

        if (action === "BuyShort") {
          action = "CoverShort";
        }

        return {
          fund: findFundAbbById(order?.fund, funds),
          strategy: strategy,
          ticker: ticker,
          custodian: custodian,
          action: action,
          shareCount: shareCount,
          tag: tag,
          broker: broker,
          originalTradeId: order?.tradeId, // Preserve the original tradeId
          tradeId: order?.tradeId, // Also keep it as tradeId for compatibility
        };
      });

      const groupedData = filteredOrders?.reduce((acc, row) => {
        if (!acc[row.ticker]) {
          acc[row.ticker] = [];
        }
        acc[row.ticker].push(row);
        return acc;
      }, {});

      const rowsWithColors = Object.entries(groupedData)?.reduce(
        (acc, [ticker, rows]) => {
          const baseGrey = 200;
          const greyOffset = 0;

          rows?.forEach((row, index) => {
            let color;
            if (row?.tag === 1) {
              const greyShade = baseGrey + ((index * greyOffset) % 400);
              color = theme.palette.grey[greyShade];
            } else if (row?.tag === 0) {
              color = "#add8e6";
            } else {
              color = theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100];
            }
            acc.push({ ...row, color });
          });
          return acc;
        },
        []
      );

      const uniqueFunds = [
        "All",
        ...new Set(rowsWithColors?.map((row) => row?.fund)),
      ];
      setUniqueFunds(uniqueFunds);

      let filteredRows =
        selectedFund === "All"
          ? rowsWithColors
          : rowsWithColors?.filter((row) => row?.fund === selectedFund);

      if (toggleFilterByCustodian) {
        // First, find all unique ticker+fund combinations that have at least one "cros" custodian
        const tickerFundPairsWithCros = filteredRows
          .filter((row) => row?.custodian === "cros")
          .map((row) => `${row.ticker}_${row.fund}`); // Create unique identifier

        // Then filter rows to include both "cros" rows and their partner rows
        filteredRows = filteredRows.filter((row) => {
          const rowIdentifier = `${row.ticker}_${row.fund}`;
          return tickerFundPairsWithCros.includes(rowIdentifier);
        });
      }

      const filteredUsers = applySortFilter(
        filteredRows,
        getComparator(order, orderBy)
      );
      setData(filteredUsers);
    }, [order, orderBy, crossingTrades, selectedFund, toggleFilterByCustodian]);

    const deleteActivity = (index) => {
      const newData = [...data];
      newData.splice(index, 1);
      setData(newData);
    };

    const Legend = () => (
      <Box sx={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", marginRight: 2 }}>
          <Box
            sx={{
              width: 20,
              height: 20,
              backgroundColor: "#add8e6",
              marginRight: 1,
            }}
          />
          <Typography variant="body2">Crossing Custodian & Broker</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              width: 20,
              height: 20,
              backgroundColor: theme.palette.grey[200],
              marginRight: 1,
            }}
          />
          <Typography variant="body2">
            Real Custodian / Crossing Broker
          </Typography>
        </Box>
      </Box>
    );

    // Check if we're loading crossing trades data
    const isLoadingCrossingTrades =
      activeRequests.includes("rebalancer_locates") || activeRequests.includes("create_fund_rebalancer_trades");

    // Show skeleton while loading
    if (isLoadingCrossingTrades) {
      return <CrossingTradesTableSkeleton />;
    }

    return (
      <>
        <Box
          sx={{
            borderRadius: "4px",
            marginTop: "1px",
            width: "100%",
            marginRight: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
            <Legend />
            <Box
              sx={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              {/* Show count of ignored trades */}
              {ignoredRows.size > 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "12px",
                    color: "warning.main",
                    fontWeight: "bold",
                  }}
                >
                  {ignoredRows.size} trade{ignoredRows.size !== 1 ? "s" : ""}{" "}
                  ignored
                </Typography>
              )}
              <select
                value={selectedFund}
                onChange={(e) => setSelectedFund(e.target.value)}
                style={{ padding: "5px", fontSize: "14px" }}
              >
                {uniqueFunds?.map((fund, index) => (
                  <option key={index} value={fund}>
                    {fund}
                  </option>
                ))}
              </select>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              marginBottom: 0,
              marginTop: "-30px",
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: "bold", fontSize: "12px" }}
            >
              ONLY SHOW CROSSING TRADES
            </Typography>
            <IconButton
              onClick={() =>
                setToggleFilterByCustodian(!toggleFilterByCustodian)
              }
              sx={{ fontSize: 40 }}
            >
              {toggleFilterByCustodian ? (
                <ToggleOnIcon sx={{ fontSize: 40, color: "success.main" }} />
              ) : (
                <ToggleOffIcon sx={{ fontSize: 40 }} />
              )}
            </IconButton>
          </Box>
          <Box
            sx={{
              border: `100px ${theme.palette.lightGrey.tableHeader}`,
              borderRadius: "4px",
              marginTop: "1px",
              width: "100%",
              marginRight: 1,
              height: "800px",
              overflowY: "scroll"
            }}
          >
            <TableContainer >
              <Table
                size="small"
                stickyHeader
                aria-label="a dense table"
              >
                <VantageListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={tableHeader}
                  noStatus={true}
                  customizeFont={12}
                  rowCount={data.length}
                  onRequestSort={handleRequestSort}
                  onIgnoreAll={handleIgnoreAll}
                  ignoredRows={ignoredRows}
                  data={data}
                />
                <TableBody>
                  {data?.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: row.color,
                        "& > .MuiTableCell-root": {
                          paddingTop: "1px",
                          paddingBottom: "0px !important",
                          lineHeight: "1.2",
                          "& .MuiInputBase-input": {
                            padding: "0px 0px !important",
                          },
                          "& .MuiIconButton-root": {
                            padding: "0px",
                          },
                        },
                        "&:hover": {
                          background: theme.palette.primary[100],
                          cursor: "pointer",
                        },
                      }}
                    >
                      <Tablecell
                        align="center"
                        style={{
                          fontSize: "12px",
                          paddingLeft: "5px",
                          borderLeft: "none",
                          borderRight: "none",
                          color: row.color === "#add8e6" ? "#ffffff" :theme.palette.text.primary,
                          borderColor: theme.palette.divider,
                        }}
                      >
                        <Checkbox
                          checked={ignoredRows.has(index)}
                          onChange={() => handleIgnoreRow(index)}
                          size="small"
                          sx={{
                            padding: "0px",
                            "& .MuiSvgIcon-root": {
                              fontSize: "16px",
                            },
                          }}
                        />
                      </Tablecell>
                      <Tablecell
                        align="left"
                        style={{
                          fontSize: "12px",
                          paddingLeft: "5px",
                          borderLeft: "none",
                          borderRight: "none",
                          color: row.color === "#add8e6" ? "#000000" :theme.palette.text.primary,
                          borderColor: theme.palette.divider,
                        }}
                      >
                        {row.strategy}
                      </Tablecell>
                      <Tablecell
                        align="left"
                        style={{
                          fontSize: "12px",
                          paddingLeft: "5px",
                          borderLeft: "none",
                          borderRight: "none",
                          color: row.color === "#add8e6" ? "#000000" :theme.palette.text.primary,
                          borderColor: theme.palette.divider,
                        }}
                      >
                        {row?.custodian}
                      </Tablecell>
                      <Tablecell
                        align="left"
                        style={{
                          fontSize: "12px",
                          paddingLeft: "5px",
                          borderLeft: "none",
                          borderRight: "none",
                          color: row.color === "#add8e6" ? "#000000" :theme.palette.text.primary,
                          borderColor: theme.palette.divider,
                        }}
                      >
                        {row?.broker}
                      </Tablecell>
                      <Tablecell
                        align="left"
                        style={{
                          fontSize: "12px",
                          paddingLeft: "5px",
                          borderLeft: "none",
                          borderRight: "none",
                          color: row.color === "#add8e6" ? "#000000" :theme.palette.text.primary,
                          borderColor: theme.palette.divider,
                        }}
                      >
                        {row?.action}
                      </Tablecell>
                      <Tablecell
                        align="left"
                        style={{
                          fontSize: "12px",
                          paddingLeft: "5px",
                          borderLeft: "none",
                          borderRight: "none",
                          color: row.color === "#add8e6" ? "#000000" :theme.palette.text.primary,
                          borderColor: theme.palette.divider,
                        }}
                      >
                        {row?.ticker?.toUpperCase() || ""}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          fontSize: "12px",
                          paddingLeft: "5px",
                          borderLeft: "none",
                          borderRight: "none",
                          color: row.color === "#add8e6" ? "#000000" :theme.palette.text.primary,
                          borderColor: theme.palette.divider,
                        }}
                      >
                        {row?.shareCount !== undefined &&
                        row?.shareCount != null
                          ? row?.shareCount < 0
                            ? `(${Math.abs(row?.shareCount)})`
                            : `${calculateAndFormatNoAbsInt(row?.shareCount)}`
                          : ""}
                      </Tablecell>
                      <Tablecell
                        align="left"
                        style={{
                          fontSize: "12px",
                          paddingLeft: "5px",
                          borderLeft: "none",
                          borderRight: "none",
                          color: row.color === "#add8e6" ? "#000000" :theme.palette.text.primary,
                          borderColor: theme.palette.divider,
                        }}
                      >
                        {row?.fund}
                      </Tablecell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </>
    );
  }
);

export default CrossingTradesTable;
